import type {
  RouteLocation,
  RouteLocationRaw,
  RouteRecordRaw,
} from "vue-router";
import { computed, h } from "vue";
import { createRouter, createWebHistory, RouterView } from "vue-router";
import { TokenService } from "@/services/token";
import UserService from "../services/user";
import { FeatureEnum } from "@/utils/userRoles";
import { eventStrings } from "@/components/demo/eventStrings";
import { updatePath } from "@/services/iframe.ts";
import { useHubUiStore, NPSSurveyActionTrigger } from "@/stores/hubUi";
import GenericWrapper from "@/components/GenericWrapper.vue";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "home",
    redirect: {
      name: "welcome",
    },
  },
  {
    path: "/welcome",
    name: "welcome",
    component: () => import("@/views/Welcome.vue"),
  },
  {
    path: "/login",
    name: "login",
    component: () => import("@/views/Login.vue"),
    meta: {
      public: true, // Allow access to even if not logged in
      onlyWhenLoggedOut: true,
    },
  },
  {
    path: "/login/sso/:idProvider/:type?",
    name: "sso-callback",
    component: () => import("@/views/SSOCallback.vue"),
    meta: {
      public: true, // Allow access to even if not logged in
      onlyWhenLoggedOut: true,
    },
  },
  {
    path: "/maintenance",
    name: "maintenance",
    component: () => import("@/views/Maintenance.vue"),
    meta: {
      public: true,
    },
  },
  {
    path: "/mcp/consent",
    name: "mcp-consent",
    component: () => import("@/views/McpConsent.vue"),
    meta: {
      // Render without waiting for the bot loader — the consent endpoints
      // authenticate via the Hub session token / request id, not a bot.
      public: true,
    },
  },
  {
    path: "/2fa/:token",
    name: "twofa",
    component: () => import("@/views/Twofa.vue"),
    meta: {
      public: true, // Allow access to even if not logged in
      onlyWhenLoggedOut: true,
    },
  },
  {
    path: "/password",
    name: "password",
    component: () => import("@/views/Password.vue"),
    meta: {
      public: true, // Allow access to even if not logged in
      onlyWhenLoggedOut: true,
    },
  },
  {
    path: "/preview/:botId/:channelId?",
    name: "preview",
    component: () => import("@/views/Preview.vue"),
    meta: {
      public: true,
    },
    props: (route: RouteLocation) => ({
      botId: route.params.botId,
      channelId: route.params.channelId,
    }),
  },
  {
    path: "/password/:username/:token/:botinvite?",
    name: "setpassword",
    component: () => import("@/views/Password.vue"),
    meta: {
      public: true, // Allow access to even if not logged in
      onlyWhenLoggedOut: true,
    },
  },
  {
    path: "/intents",
    name: "intents",
    component: () => import("@/views/Intents.vue"),
    meta: {
      simpleEdit: true,
    },
  },
  {
    path: "/intent/:name",
    name: "intentsedit",
    // New ShadCN chrome (sidebar + app bar, contained scroll) instead of the
    // legacy GenericHubWrapper.
    component: RouterView,
    redirect: { name: "intent-view" },
    children: [
      {
        name: "intent-view",
        path: ":view?",
        component: () =>
          import("@/components/intent/views/ContentEditorContainer.vue"),
      },
    ],
    meta: {
      simpleEdit: true,
      // Keeps the "Intents" sidebar entry active (its path is not below /intents).
      menu: "intents",
    },
  },
  {
    path: "/forms",
    name: "Forms",
    component: () => import("@/views/Forms.vue"),
    meta: {
      formView: true,
      simpleEdit: true,
    },
  },
  {
    path: "/forms/:name",
    // ContentEditorContainer supplies its own ShadCN chrome (app bar + sidebar)
    // via GenericWrapper, so the route mounts a bare RouterView instead of the
    // legacy GenericHubWrapper — otherwise the two app bars stack. Mirrors the
    // /intent/:name route.
    name: "forms",
    component: RouterView,
    redirect: { name: "Forms" },
    children: [
      {
        name: "form-view",
        path: ":view?",
        component: () =>
          import("@/components/intent/views/ContentEditorContainer.vue"),
      },
      //{
      //  meta: {
      //    admin: true,
      //  },
      //  path: 'training',
      //  name: 'training',
      //  component: TrainingdataManagement,
      //},
    ],
    meta: {
      simpleEdit: true,
      formView: true,
    },
  },
  // TODO comment in if you want to use the new forms view
  // {
  //   path: '/forms/:name',
  //   name: 'forms',
  //   component: () => import('@/views/GenericHubWrapper.vue'),// BotForms,
  //   redirect: { name: "Forms" },
  //   meta: {
  //     formView: true,
  //     simpleEdit: true,
  //   },
  //   children: [
  //   {
  //     name: "form-view",
  //     path: ":view?",
  //     component: () => import('@/components/intent/views/ContentEditorContainer.vue'),
  //   }
  //   ]
  // },
  {
    // Overview is a top-level route so it owns its chrome via GenericWrapper
    // (app bar + the new ShadCN sidebar), mirroring /forms and /intents.
    path: "/conversational",
    name: "conversational",
    component: () => import("@/views/Conversational.vue"),
    meta: {
      conversationalView: true,
      simpleEdit: true,
    },
  },
  {
    // ContentEditorContainer supplies its own ShadCN chrome (app bar + sidebar)
    // via GenericWrapper, so the route mounts a bare RouterView instead of the
    // legacy GenericHubWrapper — otherwise the two app bars stack (double top
    // bar). Mirrors the /intent/:name route. The :view? child is named
    // `conversationaledit` (EditorView relies on that route name).
    path: "/conversational/:name",
    component: RouterView,
    meta: {
      conversationalView: true,
      simpleEdit: true,
    },
    children: [
      {
        path: ":view?",
        name: "conversationaledit",
        component: () =>
          import("@/components/intent/views/ContentEditorContainer.vue"),
      },
    ],
  },
  {
    path: "/advisors",
    name: "advisors",
    component: () => import("@/views/ProductAdvisor.vue"),
    redirect: { name: "advisor-list" },
    children: [
      {
        path: "",
        name: "advisor-list",
        component: () =>
          import("@/components/productAdvisor/ProductAdvisorOverview.vue"),
      },
      {
        path: ":advisorId",
        name: "advisor",
        // Empty <RouterView> needed to display children
        component: { render: () => h(RouterView) },
        children: [
          {
            path: "",
            name: "edit-advisor",
            component: () =>
              import("@/components/productAdvisor/EditProductAdvisor.vue"),
          },
          {
            path: "features",
            name: "advisor-features",
            component: () =>
              import("@/components/productAdvisor/ProductAdvisorFeatures.vue"),
            meta: {
              owner: true,
            },
          },
          {
            path: "products",
            name: "advisor-products",
            component: () =>
              import("@/components/productAdvisor/ProductAdvisorProducts.vue"),
            meta: {
              admin: true,
            },
          },
          {
            path: "product/:productId?",
            name: "advisor-edit-product",
            component: () =>
              import("@/components/productAdvisor/ProductAdvisorProduct.vue"),
          },
        ],
      },
    ],
    meta: {
      simpleEdit: true,
    },
  },
  {
    path: "/licence",
    name: "licence",
    component: () =>
      import(/* webpackChunkName: "licence-view" */ "@/views/Licence.vue"),
    meta: {
      admin: true,
      routeOnBotChange: "licence",
    },
  },
  {
    path: "/licences",
    name: "licences",
    component: () =>
      import(/* webpackChunkName: "licences-view" */ "@/views/Licences.vue"),
    meta: {
      admin: true,
      routeOnBotChange: "licences",
    },
  },
  {
    path: "/validator",
    name: "validator",
    component: () =>
      import(
        /* webpackChunkName: "bot-validator-view" */ "@/views/BotValidator.vue"
      ),
    meta: {
      admin: true,
    },
  },
  {
    path: "/bot-settings",
    name: "bot-settings",
    component: () =>
      import(
        /* webpackChunkName: "bot-setting-view" */ "@/views/BotSetting.vue"
      ),
    meta: {
      admin: true,
    },
  },
  {
    path: "/bot",
    name: "bot",
    component: () =>
      import(
        /* webpackChunkName: "bot-setting-view" */ "@/views/BotSetting.vue"
      ),
    meta: {
      admin: true,
    },
  },
  {
    path: "/bots",
    name: "bots",
    component: () => import("@/views/Bots.vue"),
    meta: {
      admin: true,
    },
  },
  {
    path: "/search",
    name: "search",
    component: () => import("@/views/SearchAndReplace.vue"),
    meta: {
      simpleEdit: true,
      admin: true,
    },
  },
  {
    path: "/ai",
    name: "ai",
    component: () => import("@/views/AIPlayground.vue"),
    props: (route) => ({
      text: route.query.text,
    }),
  },
  {
    path: "/settings",
    name: "settings",
    component: () => import("@/views/Settings.vue"),
    meta: {
      owner: true,
    },
  },
  {
    path: "/integrations",
    name: "integrations",
    component: () => import("@/views/Integrations.vue"),
    meta: {
      adminOrOwnerIntegration: true,
    },
  },
  {
    path: "/integrations/:type_id/settings",
    name: "integrations-settings",
    component: () => import("@/views/IntegrationsSettings.vue"),
    meta: {
      adminOrOwnerIntegration: true,
    },
  },
  {
    path: "/integrations/:type_id",
    name: "integrations-detail",
    component: () => import("@/views/IntegrationsDetail.vue"),
    meta: {
      adminOrOwnerIntegration: true,
    },
  },
  {
    path: "/integrations/:type_id/:key",
    name: "integrations-config",
    component: () => import("@/views/IntegrationsConfig.vue"),
    meta: {
      adminOrOwnerIntegration: true,
    },
  },
  {
    name: "knowledge-base",
    path: "/knowledge-base",
    component: () => import("@/views/KnowledgeBase.vue"),
    redirect: { name: "knowledge-base-container" },
    meta: {
      simpleEdit: true,
    },
    children: [
      {
        name: "knowledge-base-container",
        path: "manage",
        component: () =>
          import("@/components/knowledgeBase/KnowledgeBaseContainer.vue"),
      },
      {
        name: "knowledge-base-edit-document",
        path: "documents/:resourceId/edit",
        component: () =>
          import("@/components/knowledgeBase/EditDocumentView.vue"),
      },
    ],
  },
  {
    path: "/settings/roles",
    name: "settings-roles",
    component: () => import("@/views/SettingsRoles.vue"),
    meta: {
      owner: true,
    },
  },
  {
    path: "/settings/privacy",
    name: "settings-privacy",
    component: () => import("@/views/SettingsPrivacy.vue"),
    meta: {
      owner: true,
    },
  },
  {
    path: "/settings/regexp",
    name: "settings-regexp",
    component: () => import("@/views/SettingsRegExp.vue"),
    meta: {
      admin: true,
    },
  },
  {
    path: "/settings/archive",
    name: "settings-archive",
    component: () => import("@/views/SettingsArchive.vue"),
    meta: {
      admin: true,
    },
  },
  {
    path: "/settings/files",
    name: "settings-files",
    component: () => import("@/views/SettingsFiles.vue"),
    meta: {
      admin: true,
    },
  },
  {
    path: "/settings/contingents",
    name: "settings-contingents",
    component: () => import("@/views/SettingsContingents.vue"),
    meta: {
      admin: true,
    },
  },
  {
    path: "/settings/channels",
    name: "settings-channels",
    component: () => import("@/views/SettingsChannels.vue"),
    meta: {
      owner: true,
    },
  },
  {
    path: "/settings/api",
    name: "settings-api",
    component: () => import("@/views/SettingsApi.vue"),
    meta: {
      owner: true,
    },
  },
  {
    path: "/settings/subcontractors",
    name: "settings-subcontractors",
    component: () => import("@/views/SettingsSubcontractors.vue"),
    meta: {
      owner: true,
    },
  },
  {
    path: "/settings/sipgate",
    name: "settings-sipgate",
    component: () => import("@/views/SettingsSipgate.vue"),
    meta: {
      owner: true,
    },
  },
  {
    path: "/settings/ai",
    name: "settings-ai",
    component: () => import("@/views/SettingsAI.vue"),
    children: [
      {
        path: "agents",
        name: "ai-settings",
        component: () =>
          import("@/components/settingsAi/channels/AISettings.vue"),
      },
    ],
    meta: {
      owner: true,
    },
  },
  {
    path: "/settings/channels/:id",
    name: "settings-channels-detail",
    component: () => import("@/views/SettingsChannelsDetail.vue"),
    meta: {
      admin: true,
    },
  },
  {
    path: "/livechat",
    name: "livechat",
    // No `meta.livechat` feature gate here (matching the old hub): users without the
    // LIVECHAT feature must reach the page so Livechat.vue can render the
    // LivechatNoPackage upsell screen with its request-feature CTA.
    component: () => import("@/views/Livechat.vue"),
    meta: {},
  },
  {
    path: "/settings/livechat",
    name: "settings-livechat",
    component: () => import("@/views/SettingsLivechat.vue"),
    meta: {
      livechatAdmin: true,
    },
  },
  {
    path: "/settings/livechat/agent/:uuid",
    name: "settings-livechat-agent",
    component: () => import("@/views/SettingsLivechatAgent.vue"),
    meta: {
      livechatAdmin: true,
    },
  },
  {
    path: "/settings/livechat/groups",
    name: "settings-livechat-groups",
    component: () => import("@/views/SettingsLivechatGroups.vue"),
    meta: {
      livechatAdmin: true,
    },
  },
  {
    path: "/settings/livechat/groups/:uuid",
    name: "settings-livechat-group-details",
    component: () => import("@/views/SettingsLivechatGroupDetails.vue"),
    meta: {
      livechatAdmin: true,
    },
  },
  {
    path: "/user-profile",
    name: "user-profile",
    component: () => import("@/views/UserProfile.vue"),
  },
  {
    path: "/iframed",
    name: "iframe",
    component: () => import("@/views/IframeContainer.vue"),
    children: [
      {
        path: "wt",
        name: "iframe:wt",
        component: () => import("@/views/WidgetTeaser.vue"),
        meta: {
          simpleEdit: true,
        },
      },
      {
        path: "wt/widget",
        name: "iframe:widget",
        component: () => import("@/views/Widget.vue"),
        meta: {
          widgetEditor: true,
          simpleEdit: true,
        },
      },
      {
        path: "wt/teaser",
        name: "iframe:teaser",
        component: () => import(/* webpackChunkName: "teasers-view" */ "@/views/Teasers.vue"),

        meta: {
          simpleEdit: true,
        },
      },
      {
        path: "wt/teaser/:id",
        name: "iframe:teaser-detail",
        component: () => import("@/views/TeasersDetail.vue"),
        meta: {
          simpleEdit: true,
        },
      },
    ],
  },
  {
    path: "/wt",
    name: "wt",
    component: () => import("@/views/WidgetTeaser.vue"),
    props: () => ({}),
    meta: {
      simpleEdit: true,
    },
  },
  {
    path: "/wt/widget",
    name: "widget",
    component: () => import("@/views/Widget.vue"),
    meta: {
      widgetEditor: true,
      simpleEdit: true,
    },
  },
  {
    path: "/wt/teaser",
    name: "teaser",
    component: () =>
      import(/* webpackChunkName: "teasers-view" */ "@/views/Teasers.vue"),

    meta: {
      simpleEdit: true,
    },
  },
  {
    path: "/wt/teaser/:id",
    name: "teaser-detail",
    component: () => import("@/views/TeasersDetail.vue"),
    meta: {
      simpleEdit: true,
    },
  },
  {
    path: "/statistics",
    name: "statistics",
    component: () => import("@/views/Stats.vue"),
  },
  {
    path: "/livechat-statistics",
    name: "livechat-statistics",
    component: () => import("@/views/LivechatStats.vue"),
  },
  {
    path: "/statistics/:type",
    name: "statsReport",
    component: () => import("@/views/StatsReport.vue"),
    props: (route: RouteLocation) => ({
      statsType: route.params.type,
    }),
  },
  {
    path: "/conversations",
    name: "conversations",
    component: () => import("@/views/Conversations.vue"),
    props: (route: RouteLocation) => ({
      channel: route.query.channel,
      from: route.query.from,
      to: route.query.to,
      filter: route.query.filter,
      intent: route.query.intent,
    }),
  },
  {
    path: "/chats",
    name: "chats",
    children: [
      {
        path: "messaging/:uuid",
        name: "chat-history",
        component: () => import("@/views/ChatHistory.vue"),
        props: (route: RouteLocation) => ({
          uniqueUserId: route.params.uuid,
          jumpto: route.query.jumpto,
        }),
      },
    ],
    component: () => import("@/views/Chats.vue"), // Chats
  },
  {
    path: "/chats/insights",
    name: "chats-insights",
    component: () => import("@/views/ChatInsights.vue"), // Chats
  },
  {
    path: "/chats/export",
    name: "chats-export",
    component: () => import("@/views/ChatExport.vue"),
    meta: {
      adminOrOwnerIntegration: true,
    },
  },
  {
    // New (Vue3) conversation insights dashboard — the eventual replacement for
    // the legacy ChatInsights view. Admins reach it via the "Conversations"
    // sidebar entry (see AppSidebar.vue); non-admins keep the legacy chats view.
    // Mirrors the old hub's admin-only Insights menu routing.
    name: "insights-new",
    path: "/insights",
    redirect: "/insights/dashboard",
    // InsightsDashboardContainer renders its own full-height grid with no
    // chrome, so it needs the wrapper to provide the app bar + sidebar. The new
    // ShadCN GenericWrapper only renders a slot (no internal RouterView), so wrap
    // a RouterView in it instead of the legacy GenericHubWrapper.
    component: { render: () => h(GenericWrapper, () => h(RouterView)) },
    meta: {
      admin: true,
    },
    children: [
      {
        name: "insights-landing",
        path: "dashboard",
        component: () =>
          import(
            "@/components/insightsDashboard/InsightsDashboardContainer.vue"
          ),
        children: [
          {
            name: "insights-inspect",
            path: "conversation/:conversationId",
            component: () =>
              import(
                "@/components/insightsDashboard/conversation/MainView.vue"
              ),
          },
        ],
      },
    ],
  },
  {
    path: "/channels",
    name: "channels",
    component: () => import("@/views/Channels.vue"),
    meta: {
      admin: true,
    },
    children: [
      {
        // UserProfile will be rendered inside User's <router-view>
        // when /user/:id/profile is matched
        path: "setting/:botId/:channelid",
        components: {
          default: () => import("@/views/Channels.vue"),
          dialog: () => import("@/views/ChannelSetting.vue"),
        },
      },
    ],
  },
  {
    path: "/notifications",
    name: "notifications",
    component: () => import("@/views/BotNotifications.vue"),
    meta: {
      admin: true,
    },
  },
  {
    path: '/deployment-details',
    name: 'deployment-details',
    component: () => import("@/views/DeploymentDetails.vue"),
    meta: {
      simpleEdit: true,
    },
  },
  {
    path: "/oauth/:provider",
    name: "oauth",
    component: () => import("@/views/OAuth.vue"),
    props: (route: RouteLocation) => ({
      code: route.query.code,
    }),
  },
  {
    path: "/qm",
    name: "admin-qm",
    component: () => import("@/views/QmDashboard.vue"),
    meta: {
      admin: true,
    },
  },
  {
    path: "/tools",
    name: "tools",
    component: () => import("@/views/ToolView.vue"),
    meta: {
      admin: true,
    },
  },
  {
    path: "/prompt-manager",
    name: "prompt-manager",
    component: () =>
      import(
        /* webpackChunkName: "prompt-manager" */ "@/views/PromptManager.vue"
      ),
    meta: {
      admin: true,
    },
  },
  {
    path: "/dreaming",
    name: "dreaming",
    component: () => import("@/views/GapFinder.vue"),
    meta: { simpleEdit: true },
  },
  {
    path: "/gap-finder",
    redirect: { name: "dreaming" },
  },
  {
    path: "/chatbot-erstellen",
    name: "demo-onboarding",
    component: () => import("@/views/DemoCreateBot.vue"),
    meta: {
      public: true,
    },
  },
  {
    path: "/create-chatbot",
    // Distinct name: Vue Router (unlike v3 in the old hub) evicts the earlier
    // route when a duplicate `name` is registered, which previously dropped
    // `/chatbot-erstellen` from the matcher and made it fall through to the 404.
    name: "demo-onboarding-create",
    component: () => import("@/views/DemoCreateBot.vue"),
    meta: {
      public: true,
    },
  },
  // The demo creation funnel (v3). In the language of the account or the browser, none in the URL.
  {
    path: "/start",
    name: "demo-start",
    component: () => import("@/views/DemoStart.vue"),
    meta: {
      public: true,
    },
  },
  // Its status page: where the funnel lands after submitting, and the link in the mails (`/start/status#<token>`)
  {
    path: "/start/status",
    name: "demo-status",
    component: () => import("@/views/DemoStart.vue"),
    meta: {
      public: true,
    },
    props: { screen: "status" },
  },
  {
    path: "/ccw",
    name: "demo-event-ccw",
    component: () => import("@/views/DemoCreateBot.vue"),
    meta: {
      public: true,
    },
    props: { event: eventStrings.ccw2024 },
  },
  {
    path: "/ccw2024",
    name: "demo-event-ccw2024",
    component: () => import("@/views/DemoCreateBot.vue"),
    meta: {
      public: true,
    },
    props: { event: eventStrings.ccw2024 },
  },
  {
    path: "/internorga",
    name: "demo-event-internorga",
    component: () => import("@/views/DemoCreateBot.vue"),
    meta: {
      public: true,
    },
    props: { event: eventStrings.internorga },
  },
  {
    path: "/omr",
    name: "demo-event-omr",
    component: () => import("@/views/DemoCreateBot.vue"),
    meta: {
      public: true,
    },
    props: { event: eventStrings.omr },
  },
  {
    path: "/sesu",
    name: "demo-event-sesu",
    component: () => import("@/views/DemoCreateBot.vue"),
    meta: {
      public: true,
    },
    props: { event: eventStrings.sesu },
  },
  {
    path: "/demo-creation",
    name: "demo-creation",
    component: () =>
      import(
        /* webpackChunkName: "demo-creation-view" */ "@/views/DemoCreation.vue"
      ),
    meta: {
      admin: true,
    },
  },
  { path: "/:pathMatch(.*)*", component: () => import("@/views/NotFound404.vue") },

];

// add route for testing component if enviroment is development
if (import.meta.env.MODE === 'development') {
  routes.push({
    path: '/livechat-agent',
    name: 'livechat-agent',
    component: () => import('@/views/LivechatAgent.vue'),
  });

  routes.push({
    path: '/livechat-agent/profile',
    name: 'livechat-agent-profile',
    component: () => import('@/views/LivechatAgentProfile.vue'),
  });
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach((to) => {

  if (import.meta.env.VITE_MAINTENANCE === 'true' && to.name !== 'maintenance') {
    return {
      path: '/maintenance',
    };
  }

  if (to.name === 'maintenance' && import.meta.env.VITE_MAINTENANCE !== 'true') {
    return {
      path: '/',
    };
  }

  const loggedIn = !!TokenService.getToken();
  const isPublic = to.matched.some((record) => record.meta.public);

  if (isPublic && (!loggedIn || to.name !== 'login')) {
    return true;
  }

  const onlyWhenLoggedOut = to.matched.some(
    (record) => record.meta.onlyWhenLoggedOut
  );
  const onlyOwner = to.matched.some((record) => record.meta.owner);
  const onlyAdmin = to.matched.some((record) => record.meta.admin);
  const onlyRAG = to.matched.some((record) => record.meta.rag);
  const onlySimpleEdit = to.matched.some((record) => record.meta.simpleEdit);
  const formView = to.matched.some((record) => record.meta.formView);
  const livechatAdmin = to.matched.some((record) => record.meta.livechatAdmin);
  const adminOrOwnerIntegration = to.matched.some((record) => record.meta.adminOrOwnerIntegration);
  // const chatInsights = to.matched.some(record => record.meta.chatInsights)
  const widgetEditorView = to.matched.some(
    (record) => record.meta.widgetEditor
  );
  const conversationalView = to.matched.some(
    (record) => record.meta.conversationalView
  );

  // legacy compatibility with # urls/links
  if (to.redirectedFrom && to.redirectedFrom.fullPath.substr(0, 2) === '/#') {
    const path = to.redirectedFrom.fullPath.substr(2);
    return { path, replace: true };
  }

  // redirect for admins to new chat insights view
  // if (to.name === 'chats' && (UserService.hasFeature(UserService.decodeToken(TokenService.getToken()), 'admin') || UserService.hasFeature(UserService.decodeToken(TokenService.getToken()), 'chatInsights')) && !to.query.old) {
  if (to.name === 'chats' && !to.query.old) {
    return {
      path: '/chats/insights',
    };
  }

  try {
    // admin user need feature
    if (
      onlyAdmin &&
      !UserService.hasFeature(
        UserService.decodeToken(TokenService.getToken()),
        FeatureEnum.ADMIN
      )
    ) {
      return {
        path: '/',
      };
    }

    // owner rule needed
    if (
      onlyOwner &&
      !UserService.hasFeature(
        UserService.decodeToken(TokenService.getToken()),
        FeatureEnum.OWNER
      ) &&
      !UserService.hasFeature(
        UserService.decodeToken(TokenService.getToken()),
        FeatureEnum.ADMIN
      )
    ) {
      return {
        path: '/',
      };
    }

    if (
      onlySimpleEdit &&
      !UserService.hasFeature(
        UserService.decodeToken(TokenService.getToken()),
        FeatureEnum.SIMPLE_EDIT
      ) &&
      !UserService.hasFeature(
        UserService.decodeToken(TokenService.getToken()),
        FeatureEnum.OWNER
      ) &&
      !UserService.hasFeature(
        UserService.decodeToken(TokenService.getToken()),
        FeatureEnum.ADMIN
      )
    ) {
      return {
        path: '/',
      };
    }

    if (
      onlyRAG && !UserService.hasFeature(
        UserService.decodeToken(TokenService.getToken()),
        FeatureEnum.RAG
      ) &&
      !UserService.hasFeature(
        UserService.decodeToken(TokenService.getToken()),
        FeatureEnum.ADMIN
      )
    ) {
      return {
        path: '/',
      };
    }

    if (
      widgetEditorView &&
      !UserService.hasFeature(
        UserService.decodeToken(TokenService.getToken()),
        FeatureEnum.WIDGET_EDITOR_VIEW
      ) &&
      !UserService.hasFeature(
        UserService.decodeToken(TokenService.getToken()),
        FeatureEnum.ADMIN
      )
    ) {
      return {
        path: '/',
      };
    }

    if (
      adminOrOwnerIntegration &&
      !UserService.hasFeature(
        UserService.decodeToken(TokenService.getToken()),
        FeatureEnum.OWNER
      ) &&
      !UserService.hasFeature(
        UserService.decodeToken(TokenService.getToken()),
        FeatureEnum.ADMIN
      )) {
      return { path: '/' };
    }

    if (
      conversationalView &&
      !UserService.hasFeature(
        UserService.decodeToken(TokenService.getToken()),
        FeatureEnum.CONVERSATIONAL_VIEW
      ) &&
      !UserService.hasFeature(
        UserService.decodeToken(TokenService.getToken()),
        FeatureEnum.ADMIN
      )
    ) {
      return {
        path: '/',
      };
    }

    if (
      formView &&
      !UserService.hasFeature(
        UserService.decodeToken(TokenService.getToken()),
        FeatureEnum.FORM_VIEW
      ) &&
      !UserService.hasFeature(
        UserService.decodeToken(TokenService.getToken()),
        FeatureEnum.ADMIN
      )
    ) {
      return {
        path: '/',
      };
    }

    // Livechat feature check for Livechat Settings
    if (
      livechatAdmin &&
      (!UserService.hasFeature(
        UserService.decodeToken(TokenService.getToken()),
        [FeatureEnum.ADMIN, FeatureEnum.OWNER] // If no features, then Redirect to Welcome page
      ) ||
        !UserService.hasFeature(
          UserService.decodeToken(TokenService.getToken()),
          FeatureEnum.LIVECHAT))
    ) {
      return {
        path: '/',
      };
    }

    if (!isPublic && !loggedIn) {
      // Hack: when urls containing /#/, they get already redirected to welcome /welcome#
      let redirect = to.fullPath;
      redirect = redirect.replace(/\/welcome#/g, '');
      return {
        path: '/login',
        query: {
          redirect,
        }, // Store the full path to redirect the user to after login
      };
    }

    // Do not allow user to visit login page or register page if they are logged in
    if (loggedIn && onlyWhenLoggedOut) {
      return '/';
    }

    return true;

  } catch (e) {
    return {
      path: '/login',
      query: {
        redirect: to.fullPath,
      },
    };
  }
});

router.afterEach((to, from) => {
  /**
   * AfterEach is also triggered on initial navigation when you load the page.
   * On the initial navigation `from` is vue-router's START_LOCATION, whose
   * `name` is undefined. Only trigger these on subsequent navigations.
   */
  if (from.name !== undefined && from.name !== null) {
    /**
     * NPS Survey hooks. No need to await them, let them run in the background.
     * The store is resolved lazily here because this guard runs at module
     * scope, before Pinia would be available at import time.
     */
    const hubUiStore = useHubUiStore();
    if (to.name === "welcome" || to.name === "home") {
      hubUiStore.tryNPSSurvey({ action: NPSSurveyActionTrigger.goToDashboard });
    } else {
      hubUiStore.tryNPSSurvey({ action: NPSSurveyActionTrigger.goToPage });
    }
  }

  return updatePath(to.fullPath.replace("/iframed", ""));
});

// Workaround for https://github.com/vitejs/vite/issues/11804
router.onError((err, to) => {
  if (err?.message?.includes?.('Failed to fetch dynamically imported module')) {
    if (localStorage.getItem('vuetify:dynamic-reload')) {
      console.error('Dynamic import error, reloading page did not fix it', err)
    } else {
      console.log('Reloading page to fix dynamic import error')
      localStorage.setItem('vuetify:dynamic-reload', 'true')
      location.assign(to.fullPath)
    }
  } else {
    console.error(err)
  }
})

router.isReady().then(() => {
  localStorage.removeItem('vuetify:dynamic-reload')
})

export default router;

/**
 * Whether a menu entry pointing at `to` should be highlighted for the route the
 * app is on. A deep page keeps its entry active: the detail page of a list, a
 * nested child route, or a route that names its entry through `meta.menu`.
 */
export const isRouteActive = computed<(to: RouteLocationRaw) => boolean>(() => {
  const route = router.currentRoute.value;

  return (to: RouteLocationRaw): boolean => {
    const resolved = router.resolve(to);

    if (resolved.name) {
      // The entry itself.
      if (resolved.name === route.name) return true;
      // A nested child route of the entry (e.g. /advisors/:advisorId).
      if (route.matched.some((record) => record.name === resolved.name)) return true;
      // A route that declares which entry it belongs to, for detail pages whose
      // path is not below the entry's (e.g. /intent/:name under /intents).
      if (route.meta?.menu === resolved.name) return true;
    }

    if (resolved.path === route.path) return true;

    // A deeper path below the entry, e.g. /forms/<id> under /forms.
    const base = resolved.path.replace(/\/+$/, '');
    return base.length > 1 && route.path.startsWith(`${base}/`);
  };
});
